// ASTRS screen help (the "?" in the ASTRS header, shown on both the Projects and Charts
// tabs). Plain words; keep it in step with tools/AstrsTool.tsx and its sections in
// tools/astrs/ProjectsSection.tsx and ChartsSection.tsx.
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const astrsHelp: ScreenHelpContent = {
  title: 'ASTRS',
  intro:
    'ASTRS has two tabs. Projects is where you plan bigger pieces of work, split into steps you can track. Charts is a blank canvas for drawing boxes and arrows, such as a flow of how something works.',
  sections: [
    {
      heading: 'Projects: your list',
      body: [
        'Press + New Project to add one. It starts with an empty title, three small boxes under it, and a grid of 3 rows by 4 columns.',
        'Use ▲ and ▼ on the left of a card to move a project up or down the list. ✕ deletes it, after you confirm. The project and any tasks made from it go to Trash, so you can restore them.',
        'Tap ▼ on the right of a card to open it; tap ▲ to close it.',
      ],
    },
    {
      heading: 'Projects: the top of a card',
      body: [
        'The big box is the project title (it says "Project title…" until you write one). Tap away from the box to save it. Tap the box area to open the Project Form. Press and hold it to give it a colour label.',
        'The three small boxes under it are for your own short notes, such as the next things to do. Press and hold one to give it a colour label.',
        'Once you have given steps a time estimate (ETC) and filled in Time Per Week in the Project Form, a line "Projected completion" shows the date you should finish at that pace.',
      ],
    },
    {
      heading: 'Projects: inside an open card',
      items: [
        { term: 'Hide Grid / Show Grid', text: 'hides or shows the grid of steps.' },
        { term: 'Hide Milestones / Show Milestones', text: 'hides or shows the milestone list.' },
        { term: 'Project Form', text: 'opens the form (see below).' },
        { term: 'Unassign List', text: 'only appears when the project is linked to a task list. It cuts that link.' },
      ],
    },
    {
      heading: 'Projects: the grid',
      body: [
        'Each box is one step. Type its name in the box. A small word on the box says where it stands. Tap the box (not the text) to change it, in this order: Inactive, Upcoming, Active, Complete, then back to Inactive. Boxes with a colour label keep their label colour and don\'t change.',
        'Press ✎ on a box to add an ETC (your time estimate, like 30m, 1h or 2d, where a day counts as 8 hours) and notes. Press Save Details to keep them. The ETC shows on the box.',
        'Press and hold a box for more: Edit Notes & ETC, Apply Label…, Send to Top (puts its name in the first small box under the title), Swap with… (then tap another box to trade places), Clear Cell, Insert Row Above or Below, Insert Column Left or Right, Delete Row, Delete Column. The two delete options ask first.',
      ],
    },
    {
      heading: 'Projects: milestones and updates',
      body: [
        'Milestones are the dates that matter. Press + Add for a new "New Milestone", rename it, tap the circle ○ to mark it done ✓, or press ✕ to remove it.',
        'Under that, Status Updates shows your five most recent notes, newest first. You add them in the Project Form.',
      ],
    },
    {
      heading: 'Projects: the Project Form',
      body: [
        'Status Update: write a note on how the project is going. It is added to the history when you save.',
        'Time Per Week: how much time you can give it (for example "10 hours"). Used for the projected completion date.',
        'Below that you may see your own questions, if you have set some up in Settings.',
        'Assign to List (Optional): pick a task list, and when you press Save Form, each of the three small boxes under the title becomes a task in that list. Pick None to turn that off.',
        'Press Save Form to keep your changes, or Cancel to leave without saving.',
      ],
    },
    {
      heading: 'Charts',
      body: [
        'Press + New Chart to start one, with a box called "Start Here". Tap a chart to open it. To delete one, press its Delete button or press and hold it.',
        'Inside a chart, name it at the top, then press a shape button (+ Rectangle, + Circle, + Diamond, + Cylinder, + Triangle, + Parallelogram) to add it. Drag a shape to move it.',
        'To join two shapes with a line, press Connect, tap the first shape ("Tap source…"), then the second ("Tap target…").',
        'Tap a shape to select it. The bar then shows colour dots to colour it and a ⋯ button to Rename or Delete it. Deleting a shape also removes its lines.',
        'Charts save by themselves a moment after you change them, but only on this phone. They don\'t sync to other devices.',
      ],
    },
  ],
};
